import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';

import {
  baoCaoGetThanhVienTeamAction,
  baoCaoClearThanhVienTeam,
} from '@app/store/reducers/baocao';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { rangeDateReport, messageToast } from '@app/utils/define';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const getQueryInt = (search, key, defaultValue = 0) => {
  try {
    const sp = new URLSearchParams(search || '');
    const raw = sp.get(key);
    const n = Number(raw);
    return Number.isFinite(n) ? n : defaultValue;
  } catch {
    return defaultValue;
  }
};

const getQueryStr = (search, key, defaultValue = '') => {
  try {
    const sp = new URLSearchParams(search || '');
    return sp.get(key) || defaultValue;
  } catch {
    return defaultValue;
  }
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return {
    from: toYMD(pair[0]),
    to: toYMD(pair[1]),
  };
};

const BaoCaoTongQuanNhanVien = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const parentReturnUrl = useMemo(() => {
    const raw = getQueryStr(location.search, 'returnUrl', '');
    if (!raw) return '';

    if (typeof raw === 'string' && raw.startsWith('/')) {
      return raw;
    }

    try {
      const decoded = decodeURIComponent(raw);
      if (decoded && decoded.startsWith('/')) {
        return decoded;
      }
    } catch {}

    return '';
  }, [location.search]);

  const returnUrl = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search || ''}`),
    [location.pathname, location.search]
  );

  const backToBang1 = useMemo(
    () => parentReturnUrl || URI_INTERNAL.BAOCAO_THONGKE_PATH,
    [parentReturnUrl]
  );

  const { thanhVienTeam, loadingThanhVienTeam, errorThanhVienTeam } = useSelector(
    (s) => s.baocao || {}
  );

  const teamId = useMemo(() => getQueryInt(location.search, 'teamId', 0), [location.search]);
  const tenTeam = useMemo(() => getQueryStr(location.search, 'tenTeam', ''), [location.search]);

  const queryFrom = useMemo(() => getQueryStr(location.search, 'From', ''), [location.search]);
  const queryTo = useMemo(() => getQueryStr(location.search, 'To', ''), [location.search]);

  const [rangeLabel, setRangeLabel] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    setFromDate(queryFrom || '');
    setToDate(queryTo || '');
  }, [queryFrom, queryTo]);

  const updateQuery = (patch = {}) => {
    const sp = new URLSearchParams(location.search || '');

    if (teamId !== null && teamId !== undefined) sp.set('teamId', String(teamId));
    if (tenTeam) sp.set('tenTeam', String(tenTeam));

    if (patch.From === null || patch.From === undefined || patch.From === '') sp.delete('From');
    else sp.set('From', String(patch.From));

    if (patch.To === null || patch.To === undefined || patch.To === '') sp.delete('To');
    else sp.set('To', String(patch.To));

    const qs = sp.toString();
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : '',
    });
  };

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      updateQuery({ From: from, To: to });
      return;
    }

    if (v === RANGE_CUSTOM) return;

    if (!v) {
      setFromDate('');
      setToDate('');
      updateQuery({ From: '', To: '' });
    }
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    updateQuery({ From: fromDate || '', To: toDate || '' });
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    updateQuery({ From: '', To: '' });
  };

  useEffect(() => {
    if (teamId === null || teamId === undefined || Number.isNaN(Number(teamId)) || Number(teamId) === 0) {
      toast.error('Thiếu teamId để xem Bảng 2.');
      history.push(URI_INTERNAL.BAOCAO_THONGKE_PATH);
      return;
    }

    dispatch(
      baoCaoGetThanhVienTeamAction({
        teamId,
        From: queryFrom,
        To: queryTo,
      })
    );

    return () => {
      dispatch(baoCaoClearThanhVienTeam());
    };
  }, [dispatch, history, teamId, queryFrom, queryTo]);

  useEffect(() => {
    if (!errorThanhVienTeam) return;
    toast.error(getErrMsg(errorThanhVienTeam) || 'Không thể tải dữ liệu Bảng 2. Vui lòng thử lại.');
  }, [errorThanhVienTeam]);

  const rows = useMemo(() => {
    if (!Array.isArray(thanhVienTeam)) return [];

    return thanhVienTeam.map((x) => {
      const nhanVienId = x?.nhanVienId ?? x?.NhanVienId ?? 0;
      const tenThanhVien = x?.tenThanhVien ?? x?.TenThanhVien ?? '';

      const tongCongViec = toNum(x?.tongCongViec ?? x?.TongCongViec ?? 0);
      const dangThucHien = toNum(x?.congViecDangThucHien ?? x?.CongViecDangThucHien ?? 0);
      const daHoanThanh = toNum(x?.congViecDaHoanThanh ?? x?.CongViecDaHoanThanh ?? 0);

      return {
        teamId,
        nhanVienId,
        tenThanhVien,
        tongCongViec,
        dangThucHien,
        daHoanThanh,
      };
    });
  }, [thanhVienTeam, teamId]);

  const detailNhanVienPath =
    URI_INTERNAL.BAOCAO_CHI_TIET_NHAN_VIEN_PATH ||
    `${URI_INTERNAL.BAOCAO_THONGKE_PATH}/chi-tiet-nhan-vien`;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-12">
          <div className="d-flex justify-content-start mb-2">
            <NavLink to={backToBang1} className="btn btn-secondary btn-sm">
              ← Quay lại
            </NavLink>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Báo cáo thống kê</h3>
            </div>

            <div className="card-body">
              <div className="mb-3">
                <div className="text-muted">
                  Tổng quan theo thành viên {tenTeam ? (<><b>({tenTeam})</b></>) : null}
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                <div style={{ minWidth: 220 }}>
                  <label className="form-label mb-1">Khoảng thời gian</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeLabel}
                    onChange={handleChangeRange}
                    disabled={loadingThanhVienTeam}
                  >
                    <option value="">-- Chọn --</option>
                    {RANGE_OPTIONS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                {rangeLabel === RANGE_CUSTOM && (
                  <>
                    <div>
                      <label className="form-label mb-1">Từ ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        disabled={loadingThanhVienTeam}
                      />
                    </div>

                    <div>
                      <label className="form-label mb-1">Đến ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        disabled={loadingThanhVienTeam}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleApplyCustomRange}
                      disabled={loadingThanhVienTeam}
                    >
                      Lọc
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClearFilter}
                  disabled={loadingThanhVienTeam}
                >
                  Xóa lọc
                </button>

                {(queryFrom || queryTo) && (
                  <div className="filter-hint" style={{ width: '100%' }}>
                    Đang lọc: {fmtDDMMYYYY(queryFrom) || '...'} → {fmtDDMMYYYY(queryTo) || '...'}
                  </div>
                )}
              </div>

              {loadingThanhVienTeam ? (
                <div>Đang tải...</div>
              ) : (
                <Table
                  striped
                  bordered
                  hover
                  className="text-center align-middle w-100"
                  style={{ tableLayout: 'fixed' }}
                >
                  <colgroup>
                    <col style={{ width: '70px' }} />
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '110px' }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th className="sorting">STT</th>
                      <th className="sorting">Thành viên</th>
                      <th className="sorting">Tổng công việc</th>
                      <th className="sorting">Đang thực hiện</th>
                      <th className="sorting">Đã hoàn thành</th>
                      <th className="sorting">Chức năng</th>
                    </tr>
                  </thead>

                  {rows && rows.length > 0 ? (
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={`${r.nhanVienId}-${idx}`}>
                          <td>{idx + 1}</td>

                          <td className="text-center">
                            <span className="d-block w-100 text-truncate" title={r.tenThanhVien}>
                              {r.tenThanhVien}
                            </span>
                          </td>

                          <td className="text-danger font-weight-bold">{r.tongCongViec}</td>
                          <td>{r.dangThucHien}</td>
                          <td>{r.daHoanThanh}</td>

                          <td>
                            <ul className="nav justify-content-center">
                              <li className="nav-item">
                                <NavLink
                                  to={`${detailNhanVienPath}?teamId=${teamId}&nhanVienId=${r.nhanVienId}${
                                    tenTeam ? `&tenTeam=${encodeURIComponent(tenTeam)}` : ''
                                  }${queryFrom ? `&From=${encodeURIComponent(queryFrom)}` : ''}${
                                    queryTo ? `&To=${encodeURIComponent(queryTo)}` : ''
                                  }&source=thanh-vien-team&sourceTab=bao-cao-thong-ke&returnUrl=${returnUrl}`}
                                  exact
                                  className="nav-link"
                                  title="Xem chi tiết"
                                >
                                  <i className="nav-icon font-icon fas fa-eye" />
                                </NavLink>
                              </li>
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr className="txt-center">
                        <td colSpan="6">Không có dữ liệu</td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaoCaoTongQuanNhanVien;
